import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StatCard, { StatCardProps } from './StatCard';
import ProjectStatusChart from './ProjectStatusChart';
import ProjectTable from './ProjectTable';
import ExchangeRateCard from './ExchangeRateCard';


interface MainGridProps {
  projects: any[];
  teamMembers: any[];
  calculateProgress: (tasks: any[]) => number;
}

export default function MainGrid({ projects, teamMembers, calculateProgress }: MainGridProps) {
  const activeProjects = projects.filter(p => p.status === 'Active' || p.status === 'In Progress').length;
  const avgCompletion = projects.length > 0 
    ? Math.round(projects.reduce((acc, p) => acc + calculateProgress(p.tasks), 0) / projects.length) 
    : 0;

  const data: StatCardProps[] = [
    {
      title: 'Total Projects',
      value: projects.length.toString(),
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [2, 3, 3, 4, 4, projects.length, projects.length],
    },
    {
      title: 'Active Projects',
      value: activeProjects.toString(),
      interval: 'Last 30 days',
      trend: projects.length > 0 ? 'up' : 'neutral',
      data: [1, 2, 2, 3, 3, activeProjects, activeProjects],
    },
    {
      title: 'Avg. Completion',
      value: `${avgCompletion}%`,
      interval: 'Last 30 days',
      trend: 'up',
      data: [45, 50, 52, 58, 62, avgCompletion, avgCompletion],
    },
    {
      title: 'Team Members',
      value: teamMembers.length.toString(),
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [4, 4, 4, 4, 4, teamMembers.length, teamMembers.length],
    },
  ];

  return (
    <Box sx={{
      width: '100%',
      maxWidth: { sm: '100%', md: '1700px' },
      height: 'calc(100vh - 90px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      gap: 2,
    }}>
      <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
        Overview
      </Typography>

      {/* Stats Cards Row - fixed height */}
      <Grid container spacing={2} columns={12} sx={{ flexShrink: 0 }}>
        {data.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Main content - fills remaining height */}
      <Grid container spacing={2} columns={12} sx={{ flex: 1, minHeight: 0 }}>
        {/* Recent Projects Table + Blank Card - left, stacked */}
        <Grid size={{ xs: 12, md: 7 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ProjectTable projects={projects} />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ExchangeRateCard />
          </Box>
        </Grid>

        {/* Charts stacked - right */}
        <Grid size={{ xs: 12, md: 5 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ProjectStatusChart projects={projects} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
